import React from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";

const GUIDE_ITEMS = [
  {
    icon: <VideocamOutlinedIcon />,
    title: "Camera",
    description:
      "If you choose Audio & Video, check that your camera is working before joining.",
  },
  {
    icon: <MicOutlinedIcon />,
    title: "Microphone",
    description:
      "Your microphone is required so your healthcare professional can hear you.",
  },
  {
    icon: <VolumeUpOutlinedIcon />,
    title: "Speaker",
    description:
      "Use the test sound to make sure you can hear the consultation clearly.",
  },
  {
    icon: <PrivacyTipOutlinedIcon />,
    title: "Privacy Mode",
    description:
      "Choose Audio Only if you do not want the consultation to use your camera.",
  },
  {
    icon: <WifiOutlinedIcon />,
    title: "Connection",
    description:
      "A stable internet connection helps keep audio and video clear during the consultation.",
  },
];

const FeatureGuideDialog = ({
  open,
  onClose,
}) => {
  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="waiting-room-guide-title"
    >
      <DialogTitle
        id="waiting-room-guide-title"
      >
        Before your consultation
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Stack spacing={2.5}>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            The waiting room lets you prepare your devices before joining the consultation.
          </Typography>

          <Stack spacing={1.5}>
            {GUIDE_ITEMS.map(
              (item) => (
                <Box
                  key={item.title}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      bgcolor:
                        "rgba(2,136,209,0.08)",
                      color:
                        "primary.main",
                      display: "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "center",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{
                        mt: 0.25,
                        lineHeight: 1.5,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
              ),
            )}
          </Stack>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor:
                "rgba(46,125,50,0.06)",
            }}
          >
            <CheckCircleOutlineOutlinedIcon
              color="success"
              fontSize="small"
              sx={{
                mt: 0.1,
              }}
            />

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Device checks only prepare your browser. You will still choose when to join the consultation.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
        >
          Got It
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeatureGuideDialog;